import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import {
    Trash2, PlusCircle,
    Calendar, Edit3, Truck, CheckCircle2,
    AlertCircle, Layers, Receipt,
    Eye, ClipboardList, Package, Database
} from "lucide-react";
import { toast } from "react-toastify";
import { useOrderStore } from "../../../store/orderStore";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import api from "../../../service/api";
import { Order, OrderBillingStatus } from "../../../types/order"; // BillingStatus tipini de import et
import { DataTableColumn } from "../../../types";
import { OrderDialog } from "./OrderDialog";
import { usePermission } from "../../../hooks/usePermission";
import { OrderDetailDialog } from "./OrderDetailDialog";
import { WarehouseTaskDetailDialog } from "../warehouse/WarehouseTaskDetailDialog";
import { WarehouseTask } from "../../../types/warehouse";
import { DateRangePicker } from "../../../components/DateRangePicker";
import { GroupFilter } from "../../../types";
import { ManualOrderSyncModal } from "../../../components/ManualOrderSyncModal";

export const OrderList = () => {
    const store = useOrderStore();
    const { deleteItem } = store;
    const { handleApiError } = useApiErrorHandler();
    const { confirm, modal: confirmModal } = useConfirmModal();
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const { canCreate, canUpdate, canDelete } = usePermission('orderModule');
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [isOrderOpen, setIsOrderOpen] = useState(false);
    const [editOrder, setEditOrder] = useState<Order | null>(null);
    const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<WarehouseTask | null>(null);

    const [filterDates, setFilterDates] = useState<(Date | null)[] | null>(null);

    const combinedFilters = React.useMemo(() => {
        const filters: GroupFilter[] = [];
        if (filterDates && Array.isArray(filterDates) && filterDates[0] && filterDates[1]) {
            const startDate = new Date(filterDates[0]);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(filterDates[1]);
            endDate.setHours(23, 59, 59, 999);
            filters.push({
                logic: "and",
                filters: [
                    { field: "createdAt", operator: "gte", value: startDate.toISOString() },
                    { field: "createdAt", operator: "lte", value: endDate.toISOString() }
                ]
            });
        }
        return filters;
    }, [filterDates]);

    // --- RENDER TEMPLATES ---
    const handleOpenDetail = (order: Order) => {
        setSelectedOrder(order);
        setIsDetailOpen(true);
    };
    const statusBodyTemplate = (row: Order) => {
        // Backend'den gelen "statuses" dizisindeki ilk veya tek elemanı alıyoruz
        const currentStatus = row.statuses && row.statuses.length > 0
            ? row.statuses[0]
            : null;

        if (!currentStatus) {
            return (
                <div className="flex items-center gap-2 px-2.5 py-1 rounded-full w-fit font-bold text-[10px] bg-gray-100 text-gray-500 border border-gray-200">
                    DURUM YOK
                </div>
            );
        }

        return (
            <div
                className="flex items-center gap-2 px-2.5 py-1 rounded-full w-fit font-bold text-[10px] border shadow-sm"
                style={{
                    backgroundColor: `${currentStatus.color}15`, // %15 opacity
                    color: currentStatus.color || '#6b7280',
                    borderColor: `${currentStatus.color}30`
                }}
            >
                <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: currentStatus.color || '#6b7280' }}
                />
                {currentStatus.name.toUpperCase()}
            </div>
        );
    };


    const billingStatusTemplate = (row: Order) => {
        // Fatura hareketini buluyoruz
        const invoice = row.accountMovements?.find(m => m.type === "INVOICE");

        const statusMap: Record<OrderBillingStatus, { label: string; color: string; icon: any; severity: string }> = {
            'BILLED': { label: 'FATURALANDI', color: 'text-green-600', icon: CheckCircle2, severity: 'success' },
            'PARTIAL': { label: 'KISMİ FATURA', color: 'text-orange-500', icon: Layers, severity: 'warning' },
            'UNBILLED': { label: 'BEKLİYOR', color: 'text-slate-400', icon: AlertCircle, severity: 'info' },
        };

        const status = statusMap[row.billingStatus || 'UNBILLED'];
        const Icon = status.icon;

        return (
            <div className="flex flex-col gap-1">
                <div className={`flex items-center gap-1.5 font-bold text-[10px] ${status.color}`}>
                    <Icon size={13} strokeWidth={3} />
                    <span>{status.label}</span>
                </div>

                {/* Eğer fatura varsa numarasını küçük bir badge olarak gösteriyoruz */}
                {invoice && (
                    <div className="text-[9px] font-mono font-semibold bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-200 w-fit">
                        {invoice.number}
                    </div>
                )}
            </div>
        );
    };

    const dateBodyTemplate = (row: Order) => (
        <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5 font-medium  ">
                <Calendar size={12} />
                <span>{new Date(row.orderDate).toLocaleDateString('tr-TR')}</span>
            </div>
            {row.deliveryDate && (
                <div className="flex items-center gap-1.5 text-[10px] text-orange-600 font-semibold">
                    <Truck size={10} />
                    <span>Teslim: {new Date(row.deliveryDate).toLocaleDateString('tr-TR')}</span>
                </div>
            )}
        </div>
    );

    const amountBodyTemplate = (row: Order) => {
        const totalDiscount = row.items?.reduce((sum, item) => sum + (item.discountAmount || 0), 0) || 0;
        const bulkDiscount = row.orderLevelBulkDiscount || 0;
        const totalDis = totalDiscount + bulkDiscount;

        return (
            <div className="flex flex-col items-end">
                <span className="font-black text-sm">
                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: row.currency || 'TRY' }).format(row.totalAmount)}
                </span>
                {totalDis > 0 && (
                    <span className="text-[10px] text-red-500 font-bold uppercase tracking-tighter mt-0.5" title="Toplam İndirim">
                        -{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: row.currency || 'TRY' }).format(totalDis)}
                    </span>
                )}
                <span className="text-[9px] font-mono italic uppercase tracking-tighter mt-0.5">Kaynak: {row.source}</span>
            </div>
        );
    };
    const accountBodyTemplate = (row: Order) => (
        <div className="flex flex-col">
            <div className="flex items-center gap-2">
                <span className="font-bold uppercase tracking-tight  ">{row.account?.name || 'Bilinmeyen Cari'}</span>
                {row.account?.isEInvoiceUser && <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded">e-Fatura</span>}
                <span className={`text-[9px] px-1 rounded ${row.account?.balance < 0 ? "bg-red-100 text-red-700  rounded" : "bg-green-100 text-green-700 px-1 rounded"}`}>
                    {row.account?.balance < 0 ? "Borçlu" : "Alacaklı"} : {Math.abs(row.account?.balance || 0).toLocaleString()} {row.currency}
                </span>

            </div>
            <div className="flex items-center gap-2 text-[10px]   italic">

                <span>{row.orderNumber}</span>              <span>•</span>  <span>{row.invoiceNumber}</span>
            </div>
        </div>
    );
    // Hata Çözümü: DataTableColumn<Order>[] tipini veriyoruz ve align değerlerini kısıtlıyoruz
    const orderColumns: DataTableColumn<Order>[] = [
        {
            field: "orderDate",
            header: "Tarih / Teslimat",
            width: "160px",
            body: dateBodyTemplate,
            sortable: true
        },
        {
            field: "billingStatus",
            header: "Fatura Durumu",
            width: "160px",
            body: billingStatusTemplate,
            sortable: true
        },
        {
            field: "account.name",
            header: "Cari & Sipariş No",
            width: "400px",
            body: accountBodyTemplate,
            sortable: true
        },
        {
            field: "status.name",
            header: "Durum",
            width: "160px",
            body: statusBodyTemplate,
            sortable: true
        },
        {
            field: "totalAmount",
            header: "Genel Toplam",
            width: "160px",
            align: "right",
            body: amountBodyTemplate,
            sortable: true
        },
        {
            field: "actions",
            header: "İşlemler",
            width: "200px",
            align: "center",
            body: (row: Order) => (
                <TableActions
                    actions={[
                        {
                            icon: Eye,
                            colorClass: "text-blue-500",
                            title: "Detay Görüntüle",
                            onClick: () => handleOpenDetail(row)
                        },
                        {
                            icon: ClipboardList,
                            colorClass: "text-violet-500",
                            title: "İş Emri Oluştur",
                            onClick: () => handleCreateWorkOrder(row),
                            show: !row.warehouseTask
                        },
                        {
                            icon: Package,
                            colorClass: "text-violet-600 bg-violet-100",
                            title: `İş Emri Detayı #${row.warehouseTask?.id} - ${row.warehouseTask?.status}`,
                            onClick: () => {
                                setSelectedTask((row.warehouseTask as unknown as WarehouseTask) || null);
                                setIsTaskDetailOpen(true);
                            },
                            show: !!row.warehouseTask
                        },
                        {
                            icon: Edit3,
                            colorClass: "text-orange-400",
                            title: "Düzenle",
                            onClick: () => {
                                setEditOrder(row);
                                setIsOrderOpen(true);
                            },
                            disabled: row.billingStatus === 'BILLED',
                            show: canUpdate
                        },
                        {
                            icon: Trash2,
                            colorClass: "text-red-400",
                            title: "Sil",
                            onClick: () => handleDeleteClick(row),
                            disabled: row.billingStatus === 'BILLED',
                            show: canDelete
                        }
                    ]}
                />
            )
        },
    ];
    const handleSaveOrder = async (payload: any) => {
        try {
            console.log(payload)
            if (payload.id) {
                // GÜNCELLEME MODU
                const orderId = payload.id;
                console.log(payload)
                // Tip güvenliği ve Prisma uyumu için ID'yi body'den çıkartıyoruz
                const { id, ...dataWithoutId } = payload;

                await store.updateItem(orderId, dataWithoutId);
                toast.success("Sipariş başarıyla güncellendi.");
            } else {
                // YENİ KAYIT MODU
                // Backend'e source bilgisini manuel eklemek istersen burada ekleyebilirsin
                const newPayload = {
                    ...payload,
                    source: "WEB_UI" // Örn: Siparişin arayüzden geldiğini belirtmek için
                };

                await store.createItem(newPayload);
                toast.success("Sipariş başarıyla oluşturuldu.");
            }

            // İşlem başarılı ise dialoğu kapat ve veriyi yenile
            setIsOrderOpen(false);
            setEditOrder(null);

            // ApiDataTable'ın veriyi otomatik yenilemesi için store.fetchData'yı tetikleyebilirsin
            // Store yapına göre: await store.fetchDataDynamic(store.lastQuery);

        } catch (error) {
            // BaseController veya Store'dan dönen detaylı hataları yakalar
            handleApiError(error);
        }
    };
    const handleDeleteClick = async (order: Order) => {
        const ok = await confirm(`Sipariş silinecektir. Emin misiniz?`);
        if (ok) {
            try {
                await deleteItem(order.id);
                toast.success("Sipariş başarıyla silindi.");
            } catch (err) { handleApiError(err); }
        }
    };

    const handleCreateWorkOrder = async (order: Order) => {
        const ok = await confirm(`"${order.orderNumber}" nolu sipariş için iş emri oluşturulacak. Onaylıyor musunuz?`);
        if (!ok) return;
        try {
            await api.post("/warehouse-task/create-from-order", { orderId: order.id });
            toast.success("İş emri başarıyla oluşturuldu!");
            await store.fetchDataDynamic(store.queryParams);
        } catch (err: any) {
            if (err?.response?.status === 409) {
                toast.warning("Bu sipariş için zaten bir iş emri mevcut.");
            } else {
                handleApiError(err);
            }
        }
    };

    const [isManualSyncOpen, setIsManualSyncOpen] = useState(false);

    return (
        <div className="h-full">
            {confirmModal}
            <EDataTable<Order>
                tableKey="order_list"
                useStore={() => store}
                dataKey="id"
                title="Sipariş Yönetimi"
                placeholder="Cari, Sipariş No veya Açıklama Ara..."
                filters={combinedFilters}
                filterBar={
                    <div className="w-full xl:w-auto flex-shrink-0 mb-2">
                        <DateRangePicker value={filterDates as any} onChange={setFilterDates as any} />
                    </div>
                }
                searchFields={[
                    { field: "description", operator: "contains" },
                    { field: "account.name", operator: "contains" },
                    { field: "orderNumber", operator: "contains" },
                    { field: "invoiceNumber", operator: "contains" },
                    { field: "externalId", operator: "contains" }
                ]}
                actions={
                    <div className="flex gap-2">
                        {canCreate && (
                            <TextButton onClick={() => {
                                setEditOrder(null);
                                setIsOrderOpen(true);
                            }}
                                className="btn-new">
                                Yeni Sipariş
                            </TextButton>
                        )}
                        <TextButton
                            onClick={() => setIsManualSyncOpen(true)}
                            className="bg-inputBg border border-inputBorder hover:bg-inputHover text-text hover:text-primary transition-all flex items-center gap-1.5 shadow-sm"
                        >
                            <Database size={15} className="text-primary" />
                            <span>SQL ile Ekle</span>
                        </TextButton>
                    </div>
                }
                columns={orderColumns}
            />
            <OrderDetailDialog
                isOpen={isDetailOpen}
                order={selectedOrder}
                onClose={() => setIsDetailOpen(false)}

            />
            <OrderDialog
                isOpen={isOrderOpen}
                onClose={() => {
                    setIsOrderOpen(false);
                    setEditOrder(null);
                }}
                onSave={handleSaveOrder}
                initialData={editOrder}
            />
            <ManualOrderSyncModal
                isOpen={isManualSyncOpen}
                onClose={() => setIsManualSyncOpen(false)}
                onSuccess={() => {
                    store.fetchDataDynamic(store.queryParams);
                }}
            />
            {selectedTask && (
                <WarehouseTaskDetailDialog
                    isOpen={isTaskDetailOpen}
                    onClose={() => setIsTaskDetailOpen(false)}
                    task={selectedTask}
                    onDelete={() => {
                        setIsTaskDetailOpen(false);
                        store.fetchDataDynamic(store.queryParams);
                    }}
                />
            )}
        </div>
    );
};